/**
 * <pe-snap-slider> — progressive enhancement for CSS scroll-snap rows.
 * Markup: <pe-snap-slider><ul data-track>…<li>slide</li>…</ul><div data-dots></div></pe-snap-slider>
 * Builds pagination dots (hidden with CSS where the row isn't scrollable) and keeps the
 * active dot in sync with the track's own scroll position.
 */
if (!customElements.get('pe-snap-slider')) {
  class PeSnapSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.dotsWrap = this.querySelector('[data-dots]');
      if (!this.track || !this.dotsWrap) return;

      this.slides = Array.from(this.track.children);
      this.dotsWrap.innerHTML = '';
      if (this.slides.length < 2) return;

      const label = this.dataset.dotLabel || 'Go to slide';
      this.dots = this.slides.map((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pe-dots__dot';
        dot.setAttribute('aria-label', `${label} ${index + 1}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsWrap.appendChild(dot);
        return dot;
      });

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      // Mark the first dot now and measure on the next frame, so page load doesn't force a synchronous layout.
      this.dots[0].setAttribute('aria-current', '');
      this.onScroll();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      const start = this.track.getBoundingClientRect().left + parseFloat(getComputedStyle(this.track).paddingLeft || '0');
      const left = this.track.scrollLeft + slide.getBoundingClientRect().left - start;
      this.track.scrollTo({ left, behavior: 'smooth' });
    }

    update() {
      if (!this.dots) return;
      const trackLeft = this.track.getBoundingClientRect().left;
      let active = 0;
      let best = Infinity;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < best) {
          best = distance;
          active = index;
        }
      });
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      if (atEnd) active = this.slides.length - 1;
      this.dots.forEach((dot, index) => {
        dot.toggleAttribute('aria-current', index === active);
      });
    }
  }
  customElements.define('pe-snap-slider', PeSnapSlider);
}
